import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Input,
  InputLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  SvgIcon,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import {
  colorDisableGrey,
  colorLinkGrey,
  colorPrimary,
  colorSecondary,
  colorTextBlack,
  colorTextBorder,
  colorTextGrey
} from '../../../config/theme';
import { useFormik } from 'formik';
import { getOptionLabel, handleError } from '../../../utils/utils';
import { UploadIcon } from '../../../utils/iconSvg';
import { CloseOutlined } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import dayjs from 'dayjs';
import SnackBarBox from '../../../components/SnackBarBox';
import { saveFlashcard } from '../../../redux/reducers/contentSlice';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

const AddNewQuestions = ({
  handleClose,
  handleSubmit,
  handleUpdate,
  data,
  contentId
}) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState('');
  const [showError, setShowError] = useState(false);
  const [labelIndex, setLabelIndex] = useState(null);
  const [showErrorMessage, setShowErrorMessage] = useState('');
  const [radioValue, setRadioValue] = useState('');
  const dispatch = useDispatch();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'error'
  });

  const { authData } = useSelector(state => state.auth);
  const {
    isSaveFlashCardLoading,
    isSaveFlashCardSuccess,
    isSaveFlashCardFailed,
    flashCardErrorContainer
  } = useSelector(state => state.content);

  const formik = useFormik({
    initialValues: {
      questionId: '',
      questionType: null,
      questionText: '',
      description: '',
      hint: '',
      answer: '',
      fileName: '',
      time: dayjs('2022-04-17T15:00:00'),
      optionRequests: [
        {
          optionText: 'Option 1',
          isAnswer: 0
        },
        {
          optionText: 'Option 2',
          isAnswer: 0
        }
      ]
    },
    onSubmit: values => {}
  });

  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => {
        setSnackbar({ ...snackbar, open: false });
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [snackbar]);

  useEffect(() => {
    if (data) {
      if (typeof data.questionId === 'string') {
        formik.setValues({
          ...formik.values,
          questionId: data?.questionId,
          questionType: data?.questionType,
          questionText: data?.questionText,
          description: data?.description,
          hint: data?.hint,
          answer: data?.answer,
          fileName: data?.fileName,
          optionRequests: data?.optionRequests,
          time: dayjs(data?.time).format('YYYY-MM-DDTHH:mm:ss')
        });

        const uploadedFile = data?.image;
        setFile(uploadedFile);
        setFileName(uploadedFile?.name);
        setPreviewUrl(URL.createObjectURL(uploadedFile));
        const fileType = uploadedFile?.name?.toLowerCase();
        setFileType(
          fileType?.endsWith('.png') ||
            fileType?.endsWith('.jpg') ||
            fileType?.endsWith('.jpeg') ||
            fileType?.endsWith('.gif') ||
            uploadedFile?.type?.startsWith('image/')
            ? 'Image'
            : ''
        );

        const result = data?.optionRequests?.find(item => item.isAnswer === 1);
        setRadioValue(result?.optionText || '');

        setProgress(100);
      } else {
        formik.setValues({
          ...formik.values,
          questionId: data?.questionId,
          questionType:
            data?.questionType == 'singleChoice'
              ? { label: 'Single Choice', id: 'singleChoice' }
              : data?.questionType == 'multipleChoice'
                ? { label: 'Multiple Choice', id: 'multipleChoice' }
                : data?.questionType == 'textField'
                  ? { label: 'Text Field', id: 'textField' }
                  : data.questionType,
          questionText: data?.questionText,
          description: data?.description,
          hint: data?.hint,
          answer:
            data?.questionType == 'textField' ||
            data.questionType?.id == 'textField'
              ? data.optionRequests[0].optionText
              : data?.answer,
          fileName: data?.fileName,
          optionRequests: data?.optionRequests,
          time: data?.time.includes('T')
            ? dayjs(data?.time).format('YYYY-MM-DDTHH:mm:ss')
            : dayjs(`2022-04-17T15:${data?.time}`)
        });

        const uploadedFile = data?.fileUrl;
        setFile(uploadedFile);
        setFileName(data?.fileName);
        setPreviewUrl(uploadedFile);
        const fileType = uploadedFile?.toLowerCase();
        setFileType(
          fileType?.endsWith('.png') ||
            fileType?.endsWith('.jpg') ||
            fileType?.endsWith('.jpeg') ||
            fileType?.endsWith('.gif') ||
            uploadedFile?.type?.startsWith('image/')
            ? 'Image'
            : ''
        );

        const result = data?.optionRequests?.find(item => item.isAnswer === 1);
        setRadioValue(result?.optionText || '');

        setProgress(100);
      }
    }
  }, []);

  useEffect(() => {
    if (showError) {
      setTimeout(() => {
        setShowError(false);
        setShowErrorMessage('');
      }, [2000]);
    }
  }, [showError]);

  const simulateUploadProgress = () => {
    let uploadProgress = 0;
    const interval = setInterval(() => {
      uploadProgress += 10;
      setProgress(uploadProgress);
      if (uploadProgress >= 100) clearInterval(interval);
    }, 300); // Simulate upload in steps
  };

  const handleFileUpload = event => {
    const uploadedFile = event.target.files[0];

    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif'];

    const isAllowedFileType =
      allowedExtensions.some(ext =>
        uploadedFile.name.toLowerCase().endsWith(ext)
      ) || uploadedFile.type.startsWith('image/');

    if (isAllowedFileType) {
      if (uploadedFile && uploadedFile.size > 10 * 1024 * 1024) {
        setShowError(true);
        setShowErrorMessage(
          'File size exceeds the 10 MB limit. Please upload a smaller file.'
        );
        event.target.value = '';
        return;
      }

      const timestamp = Date.now();

      const originalFileName = uploadedFile.name;
      const fileExtension = originalFileName.split('.').pop(); // Get the file extension
      const newFileName = `${originalFileName.split('.')[0]}_${timestamp}.${fileExtension}`; // e.g., "file_1618354532904.txt"

      const newFile = new File([uploadedFile], newFileName, {
        type: uploadedFile.type
      });

      setFile(newFile);

      formik.setValues({
        ...formik.values,
        fileName: newFile.name
      });
      setFileName(uploadedFile.name);

      const fileType = uploadedFile.name.toLowerCase();

      setFileType(
        fileType.endsWith('.png') ||
          fileType.endsWith('.jpg') ||
          fileType.endsWith('.jpeg') ||
          fileType.endsWith('.gif') ||
          uploadedFile.type.startsWith('image/')
          ? 'Image'
          : ''
      );

      setPreviewUrl(URL.createObjectURL(uploadedFile));

      simulateUploadProgress();
    } else {
      setShowError(true);
      setShowErrorMessage('Uploaded file type is not supported.');
    }
    event.target.value = '';
  };

  const handleRemoveFile = () => {
    setFile(null);
    setProgress(0);
    setFileType('');
  };

  const isChoiceQuestion =
    formik.values.questionType?.id === 'singleChoice' ||
    formik.values.questionType?.id === 'multipleChoice';

  const hasFilledOptions = formik.values.optionRequests.filter(option =>
    option.optionText?.trim()
  );

  const hasInvalidOption =
    isChoiceQuestion &&
    formik.values.optionRequests.some((opt, i) => {
      const text = opt.optionText?.trim();
      if (!text) {
        return true;
      }
      return text === `Option ${i + 1}`;
    });

  const hasSelectedAnswer =
    !isChoiceQuestion ||
    formik.values.optionRequests.some(option => option.isAnswer === 1);

  const areAllRequiredFieldsFilled = () => {
    if (!formik.values.questionType || !formik.values.questionText?.trim()) {
      return false;
    }

    if (!formik.values.time || !dayjs(formik.values.time).isValid()) {
      return false;
    }

    if (isChoiceQuestion) {
      return (
        hasFilledOptions.length > 1 && hasSelectedAnswer && !hasInvalidOption
      );
    }

    return Boolean(formik.values.answer?.trim());
  };

  const isSaveDisabled =
    !areAllRequiredFieldsFilled() || isSaveFlashCardLoading;

  const getSaveDisabledReason = () => {
    if (!formik.values.questionType) {
      return 'Select a question type.';
    }
    if (!formik.values.questionText?.trim()) {
      return 'Enter the question text.';
    }
    if (!formik.values.time || !dayjs(formik.values.time).isValid()) {
      return 'Set the question time.';
    }
    if (isChoiceQuestion) {
      if (hasFilledOptions.length < 2) {
        return 'Add at least two options.';
      }
      if (hasInvalidOption) {
        return 'Fill every option before saving.';
      }
      if (!hasSelectedAnswer) {
        return 'Select the correct answer using the radio button.';
      }
    }
    if (
      formik.values.questionType?.id === 'textField' &&
      !formik.values.answer?.trim()
    ) {
      return 'Enter the answer for this text field question.';
    }
    return '';
  };

  const resetQuestionForm = () => {
    formik.setValues({
      ...formik.values,
      questionId: '',
      questionType: null,
      questionText: '',
      description: '',
      hint: '',
      answer: '',
      fileName: '',
      time: dayjs('2022-04-17T15:00:00'),
      optionRequests: [
        { optionText: 'Option 1', isAnswer: 0 },
        { optionText: 'Option 2', isAnswer: 0 }
      ]
    });
    setFile(null);
    setRadioValue('');
  };

  const saveStartedInThisModal = useRef(false);

  useEffect(() => {
    if (isSaveFlashCardLoading) {
      saveStartedInThisModal.current = true;
    }
  }, [isSaveFlashCardLoading]);

  useEffect(() => {
    if (
      saveStartedInThisModal.current &&
      !isSaveFlashCardLoading &&
      isSaveFlashCardSuccess
    ) {
      saveStartedInThisModal.current = false;
      handleClose();
      resetQuestionForm();
    }
  }, [isSaveFlashCardLoading, isSaveFlashCardSuccess]);

  useEffect(() => {
    if (!isSaveFlashCardLoading && isSaveFlashCardFailed) {
      setSnackbar({
        open: true,
        message: handleError(flashCardErrorContainer?.errorMessage),
        type: 'error'
      });
    }
  }, [isSaveFlashCardLoading, isSaveFlashCardFailed, flashCardErrorContainer]);

  const getOptionRequestsForSave = () => {
    if (formik.values.questionType?.id === 'textField') {
      return [
        {
          optionText: formik.values.answer,
          isAnswer: 1
        }
      ];
    }

    return formik.values.optionRequests.filter(option =>
      option.optionText?.trim()
    );
  };

  const handleAdd = method => {
    const effectiveContentId =
      contentId || Number(localStorage.getItem('localContentId')) || 0;

    if (!effectiveContentId) {
      setSnackbar({
        open: true,
        message: 'Please save the flashcard overview before adding questions.',
        type: 'error'
      });
      return;
    }

    if (method === 'Edit') {
      let question = {
        questionId: formik.values.questionId,
        questionText: formik.values.questionText || '',
        questionType: formik.values.questionType?.id || '',
        description: formik.values.description || '',
        hint: formik.values.hint || '',
        fileName: typeof file !== 'string' ? formik.values.fileName : '',
        time: dayjs(formik.values.time).format('mm:ss'),
        optionRequests: getOptionRequestsForSave()
      };

      const flashCardPayload = {
        contentId: effectiveContentId,
        questionRequests: question || {},
        loggedInUserName: authData?.userName || ''
      };

      const formData = new FormData();
      if (typeof file !== 'string') {
        formData.append('multipartFileList', file);
      } else {
        formData.append('multipartFileList', null);
      }

      formData.append('flashCard', JSON.stringify(flashCardPayload));
      dispatch(saveFlashcard(formData));
      return;
    }

    let question = {
      questionId: null,
      questionText: formik.values.questionText || '',
      questionType: formik.values.questionType?.id,
      description: formik.values.description || '',
      hint: formik.values.hint || '',
      fileName: formik.values.fileName || '',
      time: dayjs(formik.values.time).format('mm:ss'),
      optionRequests: getOptionRequestsForSave()
    };

    const flashCardPayload = {
      contentId: effectiveContentId,
      questionRequests: question || [],
      loggedInUserName: authData?.userName || ''
    };

    const formData = new FormData();
    if (file) {
      formData.append('multipartFileList', file);
    } else {
      formData.append('multipartFileList', null);
    }

    formData.append('flashCard', JSON.stringify(flashCardPayload));
    dispatch(saveFlashcard(formData));
  };

  const handleCloseModal = () => {
    formik.setValues({
      ...formik.values,
      questionId: '',
      questionType: null,
      questionText: '',
      description: '',
      hint: '',
      answer: '',
      fileName: ''
    });
    setFile(null);
    handleClose();
  };

  const handleOnChangeLabel = (e, index) => {
    const { value } = e.target;
    const updatedOptionRequests = [...formik.values.optionRequests];
    const previousText = updatedOptionRequests[index]?.optionText || '';

    updatedOptionRequests[index] = {
      ...updatedOptionRequests[index],
      optionText: value
    };

    if (radioValue === previousText) {
      setRadioValue(value);
      updatedOptionRequests[index].isAnswer = 1;
    }

    formik.setFieldValue('optionRequests', updatedOptionRequests);
  };

  const handleChangeCheckBox = (e, index, isMultiple) => {
    const { checked } = e.target;
    if (isMultiple) {
      const updatedOptionRequests = [...formik.values.optionRequests];

      updatedOptionRequests[index] = {
        ...updatedOptionRequests[index],
        isAnswer: checked ? 1 : 0
      };

      formik.setValues({
        ...formik.values,
        optionRequests: updatedOptionRequests
      });
    } else {
      const updatedOptionRequests = formik.values.optionRequests.map(
        (item, i) => {
          if (i === index) {
            return {
              ...item,
              isAnswer: checked ? 1 : 0
            };
          }

          return {
            ...item,
            isAnswer: 0
          };
        }
      );

      formik.setValues({
        ...formik.values,
        optionRequests: updatedOptionRequests
      });
    }
  };

  // const handleAddNewLabel = () => {
  //   setLabelIndex(null);
  //   let option = {
  //     optionText: `Option ${formik.values.optionRequests?.length + 1}`,
  //     isAnswer: 0
  //   };

  //   formik.setValues({
  //     ...formik.values,
  //     optionRequests: [...formik.values.optionRequests, option]
  //   });
  // };

  const handleAddNewLabel = () => {
    setLabelIndex(null);

    const currentOptions = formik.values.optionRequests || [];

    const hasEmpty = currentOptions.some((opt, i) => {
      const isPlaceholder = opt.optionText?.trim() === `Option ${i + 1}`;
      const isEmpty = !opt.optionText?.trim();
      return isEmpty || isPlaceholder;
    });

    if (hasEmpty) {
      setSnackbar({
        open: true,
        message: 'Please fill the previous option before adding a new one.',
        type: 'error'
      });
      return;
    }

    if (currentOptions.length >= 4) {
      setSnackbar({
        open: true,
        message: 'You can add up to 4 options only.',
        type: 'warning'
      });
      return;
    }

    const newOption = {
      optionText: `Option ${currentOptions.length + 1}`,
      isAnswer: 0
    };

    formik.setFieldValue('optionRequests', [...currentOptions, newOption]);
    setLabelIndex(currentOptions.length);
  };

  const handleRemoveLabel = indexId => {
    setLabelIndex(null);
    const removedOption = formik.values.optionRequests[indexId];
    const tempOptionRequest = formik.values.optionRequests.filter(
      (item, index) => index !== indexId
    );

    if (removedOption?.optionText === radioValue) {
      setRadioValue('');
    }

    formik.setFieldValue('optionRequests', tempOptionRequest);
  };

  const handleLabel = index => {
    setLabelIndex(index);

    formik.setValues({
      ...formik.values,
      optionRequests: formik.values.optionRequests.map((item, i) => {
        if (i === index) {
          return { ...item, optionText: '' };
        }
        return item; // Keep other items unchanged
      })
    });
  };

  // Radio On Change
  const handleOnChangeRadio = e => {
    const { value } = e.target;
    setRadioValue(value);

    let tempQuestionList = [...formik.values.optionRequests];

    tempQuestionList = tempQuestionList.map(item => ({
      ...item,
      isAnswer: item.optionText === value ? 1 : 0
    }));

    formik.setFieldValue('optionRequests', tempQuestionList);
  };

  // Time Field OnChange
  const handleTimeChange = (event, newValue) => {
    const updatedTime = dayjs(event);
    if (updatedTime.isValid()) {
      const mergedTime = dayjs(formik.values.time)
        .hour(updatedTime.hour())
        .minute(updatedTime.minute())
        .second(updatedTime.second());

      formik.setFieldValue('time', mergedTime);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        backgroundColor: 'white',
        width: '900px',
        height: '80vh',
        flexDirection: 'column',
        overflowY: 'auto',
        borderRadius: '8px',
        justifyContent: 'space-between'
      }}
      className="custom_scrollable_container"
    >
      <Grid sx={{ padding: '24px', width: '100%' }}>
        <Typography
          fontSize={'20px'}
          color={colorTextBlack}
          fontWeight={'700'}
          mb={2}
        >
          {data ? 'Edit Question' : 'Add New Question'}
        </Typography>

        <Grid
          container
          display={'flex'}
          flexDirection={'column'}
          elevation={0}
          component={Paper}
          borderRadius={'8px'}
        >
          <Grid sx={{ padding: '18px 0px' }} width={'100%'}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Box sx={{ width: '50%' }}>
                <Box mb={2}>
                  <InputLabel
                    required
                    sx={{
                      fontWeight: '700',
                      color: colorTextBlack,
                      fontSize: '16px',
                      mb: 0.6
                    }}
                  >
                    Question type
                  </InputLabel>
                  <FormControl variant="outlined" fullWidth size="small">
                    <Autocomplete
                      size="small"
                      value={formik.values?.questionType}
                      onChange={(event, newValue) => {
                        if (newValue?.id !== 'textField') {
                          formik.setValues({
                            ...formik.values,
                            questionType: newValue,
                            questionText: '',
                            description: '',
                            hint: '',
                            fileName: '',
                            time: dayjs('2022-04-17T15:00:00'),
                            optionRequests: [
                              {
                                optionText: 'Option 1',
                                isAnswer: 0
                              },
                              {
                                optionText: 'Option 2',
                                isAnswer: 0
                              }
                            ]
                          });
                          setFile(null);
                          setRadioValue('');
                        } else {
                          formik.setValues({
                            ...formik.values,
                            questionType: newValue,
                            questionText: '',
                            description: '',
                            hint: '',
                            fileName: '',
                            time: dayjs('2022-04-17T15:00:00'),
                            optionRequests: []
                          });
                          setFile(null);
                          setRadioValue('');
                        }
                      }}
                      getOptionLabel={getOptionLabel}
                      disableClearable
                      isOptionEqualToValue={(option, value) => option === value}
                      options={[
                        { label: 'Single Choice', id: 'singleChoice' },
                        { label: 'Multiple Choice', id: 'multipleChoice' },
                        { label: 'Text Field', id: 'textField' }
                      ]}
                      renderInput={params => (
                        <TextField
                          name="questionType"
                          {...params}
                          placeholder="Select question type"
                          error={
                            formik.touched?.questionType &&
                            Boolean(formik.errors?.questionType)
                          }
                          helperText={
                            formik.touched?.questionType &&
                            formik.errors?.questionType
                          }
                        />
                      )}
                      ListboxProps={{
                        sx: {
                          '& .MuiAutocomplete-option:hover': {
                            backgroundColor: colorSecondary
                          }
                        }
                      }}
                    />
                  </FormControl>
                </Box>

                {formik.values.questionType?.id !== 'textField' && (
                  <Box mb={2}>
                    <InputLabel
                      required
                      sx={{
                        fontWeight: '700',
                        color: colorTextBlack,
                        fontSize: '16px',
                        mb: 0.6
                      }}
                    >
                      Add question time
                    </InputLabel>
                    <FormControl
                      variant="outlined"
                      style={{
                        width: '35%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center'
                      }}
                      size="small"
                    >
                      <Typography
                        style={{
                          fontFamily: 'Lato',
                          fontSize: '16px',
                          fontWeight: 700,
                          color: colorLinkGrey,
                          lineHeight: '20px',
                          marginRight: '20px'
                        }}
                      >
                        Time
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimeField
                          value={formik.values.time}
                          format="mm:ss"
                          size="small"
                          onChange={handleTimeChange}
                        />
                      </LocalizationProvider>
                    </FormControl>
                  </Box>
                )}

                {formik.values.questionType && (
                  <Box mb={2}>
                    <InputLabel
                      required
                      sx={{
                        fontWeight: '700',
                        color: colorTextBlack,
                        fontSize: '16px',
                        mb: 0.6
                      }}
                    >
                      Add a question
                    </InputLabel>
                    <FormControl variant="outlined" fullWidth size="small">
                      <TextField
                        maxRows={4}
                        name="questionText"
                        placeholder="Enter question"
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 200 }}
                        value={formik.values?.questionText}
                        onChange={formik.handleChange}
                        error={
                          formik.touched?.questionText &&
                          Boolean(formik.errors?.questionText)
                        }
                      />
                      <div
                        style={{
                          display: 'flex',

                          justifyContent: 'flex-end'
                        }}
                      >
                        <FormHelperText id="questionText">
                          {formik.touched?.questionText &&
                            formik.errors?.questionText}
                        </FormHelperText>
                        <Typography variant="body2" color="textSecondary">
                          {formik.values?.questionText
                            ? formik.values?.questionText?.length
                            : 0}
                          /200
                        </Typography>
                      </div>
                    </FormControl>
                  </Box>
                )}

                {formik.values.questionType?.id == 'textField' && (
                  <Box mb={2}>
                    <InputLabel
                      required
                      sx={{
                        fontWeight: '700',
                        color: colorTextBlack,
                        fontSize: '16px',
                        mb: 0.6
                      }}
                    >
                      Answer
                    </InputLabel>
                    <FormControl variant="outlined" fullWidth size="small">
                      <TextField
                        name="answer"
                        placeholder="Enter answer"
                        variant="outlined"
                        size="small"
                        maxRows={3}
                        sx={{
                          '& textarea': {
                            '&::-webkit-scrollbar': {
                              width: '0.2em'
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f5f5f5',
                              width: '3px',
                              borderRadius: '10px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: '#4E565F',
                              borderRadius: '10px',
                              border: '2px solid #4E565F'
                            }
                          }
                        }}
                        inputProps={{
                          maxLength: 300
                        }}
                        multiline
                        value={formik.values?.answer}
                        error={
                          formik.touched?.answer &&
                          Boolean(formik.errors?.answer)
                        }
                        onChange={formik.handleChange}
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <FormHelperText id="answer">
                          {formik.touched?.answer && formik.errors?.answer}
                        </FormHelperText>
                        <Typography variant="body2" color="textSecondary">
                          {formik.values?.answer
                            ? formik.values?.answer?.length
                            : 0}
                          /300
                        </Typography>
                      </div>
                    </FormControl>
                  </Box>
                )}

                {/* For CheckBoxes */}
                {formik.values.questionType?.id == 'multipleChoice' && (
                  <>
                    {formik.values.optionRequests?.map((item, index) => {
                      return (
                        <div
                          style={{
                            display: 'flex',
                            border: `1px solid ${colorDisableGrey}`,
                            borderRadius: '8px',
                            alignItems: 'center',
                            marginTop: '10px',
                            paddingBottom: labelIndex === index && '10px',
                            justifyContent: 'space-between'
                          }}
                          key={index}
                          onMouseLeave={() => setLabelIndex(null)}
                        >
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Checkbox
                              size="small"
                              checked={item.isAnswer == 1 ? true : false}
                              style={{ marginRight: '10px' }}
                              onChange={e =>
                                handleChangeCheckBox(
                                  e,
                                  index,
                                  formik.values.questionType?.id !==
                                    'checkboxes'
                                    ? true
                                    : false
                                )
                              }
                            />

                            {labelIndex === index ? (
                              <FormControl variant="standard">
                                <Input
                                  id="standard-adornment-weight"
                                  aria-describedby="standard-weight-helper-text"
                                  inputProps={{
                                    'aria-label': 'weight',
                                    autoComplete: 'off',
                                    form: {
                                      autoComplete: 'off'
                                    }
                                  }}
                                  placeholder="Add option"
                                  value={item.optionText}
                                  onChange={e => handleOnChangeLabel(e, index)}
                                  sx={{ width: '300px' }}
                                />
                              </FormControl>
                            ) : (
                              <Typography
                                style={{
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  lineHeight: '18px',
                                  fontFamily: 'Lato',
                                  color: colorLinkGrey
                                }}
                                onClick={() => handleLabel(index)}
                              >
                                {item.optionText}
                              </Typography>
                            )}
                          </div>

                          <SvgIcon
                            sx={{
                              height: '12px',
                              width: '12px',
                              marginRight: '10px',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleRemoveLabel(index)}
                          >
                            <svg
                              width="10"
                              height="9"
                              viewBox="0 0 10 9"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.33366 1.16797L1.66699 7.83464M1.66699 1.16797L8.33366 7.83464"
                                stroke="#4E565F"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </SvgIcon>
                        </div>
                      );
                    })}

                    {formik.values.optionRequests?.length < 4 && (
                      <div
                        style={{
                          display: 'flex',
                          border: `1px solid ${colorDisableGrey}`,
                          borderRadius: '8px',
                          alignItems: 'center',
                          marginTop: '10px',
                          cursor: 'pointer'
                        }}
                        onClick={handleAddNewLabel}
                      >
                        <Checkbox
                          size="small"
                          checked={false}
                          style={{ marginRight: '10px' }}
                        />
                        <Typography
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '18px',
                            fontFamily: 'Lato',
                            color: colorTextBorder
                          }}
                        >
                          Add option
                        </Typography>
                      </div>
                    )}
                  </>
                )}

                {/* For Radio Button */}
                {formik.values.questionType?.id == 'singleChoice' && (
                  <>
                    <RadioGroup
                      value={radioValue == '' ? null : radioValue}
                      onChange={handleOnChangeRadio}
                    >
                      {formik.values.optionRequests?.map((item, index) => {
                        return (
                          <div
                            style={{
                              display: 'flex',
                              border: `1px solid ${colorDisableGrey}`,
                              borderRadius: '8px',
                              alignItems: 'center',
                              marginTop: '10px',
                              paddingBottom: labelIndex === index && '10px',
                              justifyContent: 'space-between'
                            }}
                            key={index}
                            onMouseLeave={() => setLabelIndex(null)}
                          >
                            <div
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <Radio
                                value={item.optionText}
                                sx={{
                                  color: colorTextGrey,
                                  '&.Mui-checked': {
                                    color: colorPrimary
                                  }
                                }}
                                id={item.optionText}
                              />

                              {labelIndex === index ? (
                                <FormControl variant="standard">
                                  <Input
                                    id="standard-adornment-weight"
                                    aria-describedby="standard-weight-helper-text"
                                    inputProps={{
                                      'aria-label': 'weight',
                                      autoComplete: 'off',
                                      form: {
                                        autoComplete: 'off'
                                      }
                                    }}
                                    placeholder="Add option"
                                    value={item.optionText}
                                    onChange={e =>
                                      handleOnChangeLabel(e, index)
                                    }
                                    sx={{ width: '300px' }}
                                  />
                                </FormControl>
                              ) : (
                                <Typography
                                  style={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '18px',
                                    fontFamily: 'Lato',
                                    color: colorLinkGrey
                                  }}
                                  onClick={() => handleLabel(index)}
                                >
                                  {item.optionText}
                                </Typography>
                              )}
                            </div>

                            <SvgIcon
                              sx={{
                                height: '12px',
                                width: '12px',
                                marginRight: '10px',
                                cursor: 'pointer'
                              }}
                              onClick={() => handleRemoveLabel(index)}
                            >
                              <svg
                                width="10"
                                height="9"
                                viewBox="0 0 10 9"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M8.33366 1.16797L1.66699 7.83464M1.66699 1.16797L8.33366 7.83464"
                                  stroke="#4E565F"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </SvgIcon>
                          </div>
                        );
                      })}
                    </RadioGroup>

                    {formik.values.optionRequests?.length < 4 && (
                      <div
                        style={{
                          display: 'flex',
                          border: `1px solid ${colorDisableGrey}`,
                          borderRadius: '8px',
                          alignItems: 'center',
                          marginTop: '10px',
                          cursor: 'pointer'
                        }}
                        onClick={handleAddNewLabel}
                      >
                        <Radio
                          disabled={true}
                          sx={{
                            color: colorTextGrey,
                            '&.Mui-checked': {
                              color: colorPrimary
                            }
                          }}
                        />
                        <Typography
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '18px',
                            fontFamily: 'Lato',
                            color: colorTextBorder
                          }}
                        >
                          Add option
                        </Typography>
                      </div>
                    )}
                  </>
                )}

                {/* Description */}
                <Box mb={2} mt={2}>
                  <InputLabel
                    sx={{
                      fontWeight: '700',
                      color: colorTextBlack,
                      fontSize: '16px',
                      mb: 0.6
                    }}
                  >
                    Description
                  </InputLabel>
                  <FormControl variant="outlined" fullWidth size="small">
                    <TextField
                      name="description"
                      placeholder="Add description"
                      variant="outlined"
                      size="small"
                      maxRows={3}
                      sx={{
                        '& textarea': {
                          '&::-webkit-scrollbar': {
                            width: '0.2em'
                          },
                          '&::-webkit-scrollbar-track': {
                            background: '#f5f5f5',
                            width: '3px',
                            borderRadius: '10px'
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#4E565F',
                            borderRadius: '10px',
                            border: '2px solid #4E565F'
                          }
                        }
                      }}
                      inputProps={{
                        maxLength: 300
                      }}
                      multiline
                      value={formik.values?.description}
                      error={
                        formik.touched?.description &&
                        Boolean(formik.errors?.description)
                      }
                      onChange={formik.handleChange}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <FormHelperText id="description">
                        {formik.touched?.description &&
                          formik.errors?.description}
                      </FormHelperText>
                      <Typography variant="body2" color="textSecondary">
                        {formik.values?.description
                          ? formik.values?.description?.length
                          : 0}
                        /300
                      </Typography>
                    </div>
                  </FormControl>
                </Box>
              </Box>

              <Divider
                orientation="vertical"
                flexItem
                style={{ marginRight: '30px', marginLeft: '30px' }}
              />
              <Box sx={{ width: '50%' }}>
                <InputLabel
                  sx={{
                    fontWeight: '700',
                    color: colorTextBlack,
                    fontSize: '16px',
                    mb: 0.6
                  }}
                >
                  Add image/gif
                </InputLabel>
                {!file && (
                  <Box
                    mb={2}
                    border={'1px dashed #ccc'}
                    borderRadius={'8px'}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height={'150px'}
                    onDrop={e => {
                      e.preventDefault();
                      handleFileUpload({
                        target: { files: e.dataTransfer.files }
                      });
                    }}
                    onDragOver={e => e.preventDefault()}
                  >
                    {/* Upload Icon with Custom Color */}
                    <Box
                      sx={{
                        height: '24px',
                        width: '24px',
                        color: '#CBD2D9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <UploadIcon sx={{ color: '#CBD2D9' }} />
                    </Box>

                    {/* Combined Text Line */}
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      fontSize={'16px'}
                      color={colorLinkGrey}
                      marginBottom={1}
                    >
                      Drag and drop or{' '}
                      <Typography
                        component="label"
                        variant="body2"
                        fontWeight={600}
                        fontSize={'16px'}
                        color="#CBD2D9"
                        sx={{
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        browse
                        <input type="file" hidden onChange={handleFileUpload} />
                      </Typography>{' '}
                      your file
                    </Typography>

                    <Typography
                      color={colorLinkGrey}
                      fontSize={'12px'}
                      fontWeight={500}
                      paddingX={4}
                    >
                      Supported file types: jpg, png, gif (Maximum size : 10 MB)
                    </Typography>
                  </Box>
                )}

                {file && (
                  <Box mt={2} width={'416px'} marginBottom={'15px'}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                      border="1px solid #E4E7EB"
                      borderRadius="8px"
                      paddingX="12px"
                    >
                      <Box display="flex" alignItems="center">
                        {fileType === 'Image' ? (
                          <SvgIcon
                            fontSize="small"
                            sx={{ fontSize: 18, marginRight: 1 }}
                          >
                            <svg
                              width="12"
                              height="16"
                              viewBox="0 0 12 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9.8 13H3.62091C3.21704 13 3.0151 13 2.92159 12.9201C2.84045 12.8508 2.79739 12.7469 2.80577 12.6405C2.81541 12.5179 2.95821 12.3751 3.24379 12.0895L8.91242 6.42091C9.17643 6.15691 9.30844 6.0249 9.46066 5.97544C9.59455 5.93193 9.73878 5.93193 9.87268 5.97544C10.0249 6.0249 10.1569 6.1569 10.4209 6.42091L13 9V9.8M9.8 13C10.9201 13 11.4802 13 11.908 12.782C12.2843 12.5903 12.5903 12.2843 12.782 11.908C13 11.4802 13 10.9201 13 9.8M9.8 13H4.2C3.0799 13 2.51984 13 2.09202 12.782C1.71569 12.5903 1.40973 12.2843 1.21799 11.908C1 11.4802 1 10.9201 1 9.8V4.2C1 3.0799 1 2.51984 1.21799 2.09202C1.40973 1.71569 1.71569 1.40973 2.09202 1.21799C2.51984 1 3.0799 1 4.2 1H9.8C10.9201 1 11.4802 1 11.908 1.21799C12.2843 1.40973 12.5903 1.71569 12.782 2.09202C13 2.51984 13 3.0799 13 4.2V9.8M6 4.66667C6 5.40305 5.40305 6 4.66667 6C3.93029 6 3.33333 5.40305 3.33333 4.66667C3.33333 3.93029 3.93029 3.33333 4.66667 3.33333C5.40305 3.33333 6 3.93029 6 4.66667Z"
                                stroke="#1F2933"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </SvgIcon>
                        ) : (
                          ''
                        )}
                        <Typography
                          variant="body1"
                          color={colorTextBlack}
                          fontSize={'12px'}
                          fontWeight={600}
                        >
                          {fileName}
                        </Typography>
                      </Box>
                      <IconButton onClick={handleRemoveFile} size="small">
                        <CloseOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                    {progress !== 100 && (
                      <>
                        <Typography
                          variant="body2"
                          color={colorLinkGrey}
                          fontSize={'12px'}
                          fontWeight={500}
                        >
                          {progress === 100
                            ? `${progress} % completed`
                            : `Upload Progress: ${progress} %`}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            mt: 1,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor:
                                progress === 100 ? '#749E35' : undefined
                            }
                          }}
                        />
                      </>
                    )}
                  </Box>
                )}
                {file && progress === 100 && (
                  <Box mt={2} width={'416px'} marginBottom={'15px'}>
                    <Card
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        maxWidth: '416px',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        mt: 2,
                        padding: '12px'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: '244px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="Uploaded thumbnail"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}
                        />
                      </Box>
                    </Card>
                  </Box>
                )}
                {formik.values.questionType?.id !== 'textField' && (
                  <Box mb={2}>
                    <InputLabel
                      sx={{
                        fontWeight: '700',
                        color: colorTextBlack,
                        fontSize: '16px',
                        mb: 0.6
                      }}
                    >
                      Hint
                    </InputLabel>
                    <FormControl variant="outlined" fullWidth size="small">
                      <TextField
                        name="hint"
                        placeholder="Add hint"
                        variant="outlined"
                        size="small"
                        maxRows={3}
                        sx={{
                          '& textarea': {
                            '&::-webkit-scrollbar': {
                              width: '0.2em'
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f5f5f5',
                              width: '3px',
                              borderRadius: '10px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: '#4E565F',
                              borderRadius: '10px',
                              border: '2px solid #4E565F'
                            }
                          }
                        }}
                        inputProps={{
                          maxLength: 300
                        }}
                        multiline
                        value={formik.values?.hint}
                        error={
                          formik.touched?.hint && Boolean(formik.errors?.hint)
                        }
                        onChange={formik.handleChange}
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <FormHelperText id="description">
                          {formik.touched?.hint && formik.errors?.hint}
                        </FormHelperText>
                        <Typography variant="body2" color="textSecondary">
                          {formik.values?.hint
                            ? formik.values?.hint?.length
                            : 0}
                          /300
                        </Typography>
                      </div>
                    </FormControl>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        sx={{ paddingLeft: '24px' }}
        mt={2}
        item
        display={'flex'}
        justifyContent={'start'}
        gap={2}
      >
        <Button
          variant="outlined"
          onClick={handleCloseModal}
          sx={{
            px: 4,
            borderColor: colorPrimary,
            borderRadius: '10px',
            color: 'black',
            borderWidth: '2px',
            height: '42px',
            width: '106px',
            '&:hover': {
              borderColor: colorPrimary,
              backgroundColor: 'transparent',
              borderRadius: '10px',
              borderWidth: '2px'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSaveDisabled}
          sx={{
            mr: 2,
            px: 4,
            borderWidth: 2,
            width: '90px',
            marginBottom: '20px'
          }}
          onClick={() => handleAdd(data ? 'Edit' : 'Add')}
        >
          {isSaveFlashCardLoading ? 'Saving...' : 'Save'}
        </Button>
      </Grid>
      {isSaveDisabled && getSaveDisabledReason() && (
        <Typography
          sx={{
            px: 3,
            pb: 2,
            color: colorLinkGrey,
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          {getSaveDisabledReason()}
        </Typography>
      )}

      {showError && (
        <SnackBarBox type="error" message={handleError(showErrorMessage)} />
      )}
      {snackbar.open && (
        <SnackBarBox
          type={snackbar.type}
          message={snackbar.message}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      )}
    </div>
  );
};

export default AddNewQuestions;
